import React, { useState } from "react";
import { FilterTab } from "../../../components/Filters";
import { scaleUnit } from "./unitScale";

type CardTheme = {
  barBackground: string;
  iconBackground: string;
  iconColor: string;
  titleColor: string;
  peakLabelColor: string;
  peakTextColor: string;
  peakDateBg: string;
  peakDateColor: string;
  peakSlotBg: string;
};

const THEME_MAP: Record<string, CardTheme> = {
  "Load Contribution": {
    barBackground: "linear-gradient(180deg, #ED9D2B 0%, #D8800F 100%)",
    iconBackground: "linear-gradient(150deg, #ED9D2B 0%, #D8800F 100%)",
    iconColor: "white",
    titleColor: "#A8540B",
    peakLabelColor: "#A8540B80",
    peakTextColor: "#A8540B",
    peakDateBg: "#A8540B15",
    peakDateColor: "#A8540B",
    peakSlotBg: "#8C8A96",
  },
  "Generation Contribution": {
    barBackground: "linear-gradient(180deg, #10B981 0%, #059669 100%)",
    iconBackground: "linear-gradient(150deg, #10B981 0%, #059669 100%)",
    iconColor: "white",
    titleColor: "#047857",
    peakLabelColor: "#04785780",
    peakTextColor: "#047857",
    peakDateBg: "#04785715",
    peakDateColor: "#047857",
    peakSlotBg: "#8C8A96",
  },
  "Net Load": {
    barBackground: "linear-gradient(180deg, #8B5CF6 0%, #7C3AED 100%)",
    iconBackground: "linear-gradient(150deg, #8B5CF6 0%, #7C3AED 100%)",
    iconColor: "white",
    titleColor: "#7139D0",
    peakLabelColor: "#7139D080",
    peakTextColor: "#7139D0",
    peakDateBg: "#6D28D915",
    peakDateColor: "#6D28D9",
    peakSlotBg: "#8C8A96",
  },
  Energy: {
    barBackground: "linear-gradient(180deg, #94A3B8 0%, #64748B 100%)",
    iconBackground: "linear-gradient(150deg, #94A3B8 0%, #64748B 100%)",
    iconColor: "white",
    titleColor: "#475569",
    peakLabelColor: "#47556980",
    peakTextColor: "#475569",
    peakDateBg: "#47556915",
    peakDateColor: "#475569",
    peakSlotBg: "#8C8A96",
  },
};

function getFallbackTheme(dotColor: string): CardTheme {
  return {
    barBackground: dotColor,
    iconBackground: dotColor,
    iconColor: "white",
    titleColor: dotColor,
    peakLabelColor: `${dotColor}80`,
    peakTextColor: dotColor,
    peakDateBg: `${dotColor}15`,
    peakDateColor: dotColor,
    peakSlotBg: "#8C8A96",
  };
}

function resolveTheme(tag?: string, dotColor?: string): CardTheme {
  const key = tag || "";
  return THEME_MAP[key] || getFallbackTheme(dotColor || "#606679");
}

function getTitleIcon(title: string, tag?: string): React.ReactNode {
  const key = tag || title;
  if (key === "Energy") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    );
  }
  if (key === "Load" || key === "Load Contribution") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="white"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    );
  }
  if (key === "Generation" || key === "Generation Contribution") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
      </svg>
    );
  }
  if (key === "Net Load") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
      </svg>
    );
  }
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

type RankRow = {
  entity: string;
  avgDemand: number;
  peakDemand: number;
  avgTemp: number | null;
  avgHumidity: number | null;
  avgSolarIrradiance: number | null;
  netLoad: number;
  entityId?: number | null;
  peakDate?: string | null;
  peakSlot?: number | null;
  pctLoadCovered?: number | null;
};

type RankTableProps = {
  title: string;
  dotColor: string;
  tag?: string;
  rows: RankRow[];
  primaryWeather: string;
  onWeatherChange: (v: string) => void;
  onRowClick?: (row: RankRow) => void;
  loadUnit?: string;
  energyUnit?: string;
  rankView?: "top" | "bottom";
  onRankViewChange?: (v: "top" | "bottom") => void;
  entityLabel?: string;
  // Click on the small subheading (with open-icon) → open full sortable list.
  onHeadingClick?: () => void;
  autoScaleUnit?: boolean;
};

const PRIMARY_WEATHER_OPTIONS = [
  "Avg Temp",
  "Avg Humidity",
  "Avg Solar Irradiance",
];

// Short display labels. Keys above stay intact for WEATHER_META lookups.
const WEATHER_SHORT_LABEL: Record<string, string> = {
  "Avg Temp": "Avg Temp",
  "Avg Humidity": "Avg Humid",
  "Avg Solar Irradiance": "Avg Irr",
};

function weatherLabel(key: string) {
  return WEATHER_SHORT_LABEL[key] ?? key;
}

function signed(val: number, decimals = 2) {
  return val.toFixed(decimals);
}

function formatNumber(val: number, maxDecimals = 2) {
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: maxDecimals,
    minimumFractionDigits: maxDecimals,
  }).format(val);
}

// Render a numeric value with its unit, optionally scaling kW/kWh→MW/MWh
// when the value has more than 3 digits. Returns {text, unit}.
function fmtScaled(
  value: number,
  unit: string,
  autoScale: boolean,
): { text: string; unit: string } {
  const s = autoScale ? scaleUnit(value, unit) : { value, unit };
  return { text: formatNumber(s.value as number, 2), unit: s.unit };
}

// slot_48 (1-48, 30-min blocks) → block start time in 24h HH:MM.
function slotToTime(slot: number | null | undefined): string | null {
  if (slot == null || slot < 1) return null;
  const mins = (slot - 1) * 30;
  const hh = String(Math.floor(mins / 60)).padStart(2, "0");
  const mm = String(mins % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

function fmtDate(iso: string) {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function PeakBadge({
  date,
  slot,
}: {
  date?: string | null;
  slot?: number | null;
}) {
  if (!date && slot == null) return null;
  return (
    <span className="text-[10px] font-medium inline-flex items-center gap-1 shrink-0 ml-1.5">
      {date && (
        <span className="px-2 py-0.5 rounded-md border border-[#E6E9EF] bg-[#f6f7fa] text-[#6E7783] whitespace-nowrap">
          {fmtDate(date)}
        </span>
      )}
      {slot != null && slotToTime(slot) && (
        <span className="text-white px-1.5 py-0.5 rounded-[6px] bg-[#A9A8B1] whitespace-nowrap">
          {slotToTime(slot)}
        </span>
      )}
    </span>
  );
}

const WEATHER_META: Record<
  string,
  { field: keyof RankRow; unit: string; decimals: number }
> = {
  "Avg Temp": { field: "avgTemp", unit: "°C", decimals: 2 },
  "Avg Humidity": { field: "avgHumidity", unit: "%", decimals: 2 },
  "Avg Solar Irradiance": {
    field: "avgSolarIrradiance",
    unit: "W/m²",
    decimals: 2,
  },
};

// ── Sortable full-list modal ─────────────────────────────────────────────────

type SortKey =
  | "peakDemand"
  | "avgDemand"
  | "weather"
  | "pctLoadCovered"
  | "netLoad";
type SortDir = "asc" | "desc";

type RankListModalProps = {
  title: string;
  tag?: string;
  dotColor: string;
  rows: RankRow[];
  loading?: boolean;
  primaryWeather: string;
  onWeatherChange: (v: string) => void;
  loadUnit?: string;
  energyUnit?: string;
  entityLabel?: string;
  onRowClick?: (row: RankRow) => void;
  onClose: () => void;
};

export function RankListModal({
  title,
  tag,
  dotColor,
  rows,
  loading = false,
  primaryWeather,
  onWeatherChange,
  loadUnit = "MW",
  energyUnit = "MWh",
  entityLabel,
  onRowClick,
  onClose,
}: RankListModalProps) {
  const isGen = tag === "Generation Contribution";
  const t = resolveTheme(tag, dotColor);
  const [sortKey, setSortKey] = useState<SortKey>("peakDemand");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [search, setSearch] = useState("");
  const [weatherOpen, setWeatherOpen] = useState(false);
  const weatherRef = React.useRef<HTMLSpanElement>(null);
  const entityPillColor = "#047857";

  React.useEffect(() => {
    if (!weatherOpen) return;
    const handler = (e: MouseEvent) => {
      if (weatherRef.current && !weatherRef.current.contains(e.target as Node))
        setWeatherOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [weatherOpen]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const weatherMeta = WEATHER_META[primaryWeather] ?? WEATHER_META["Avg Temp"];

  const sortedRows = React.useMemo(() => {
    const valueOf = (r: RankRow): number | null => {
      if (sortKey === "weather") return r[weatherMeta.field] as number | null;
      if (sortKey === "pctLoadCovered") return r.pctLoadCovered ?? null;
      if (sortKey === "netLoad") return r.netLoad;
      return r[sortKey] as number;
    };
    const arr = [...rows];
    arr.sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      // Push nulls to the bottom regardless of direction.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [rows, sortKey, sortDir, weatherMeta.field]);

  const visibleRows = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedRows;
    return sortedRows.filter((r) => r.entity.toLowerCase().includes(q));
  }, [sortedRows, search]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ active }: { active: boolean }) => (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      className="inline-block ml-1 align-middle"
      style={{
        opacity: active ? 1 : 0.3,
        transform: active && sortDir === "asc" ? "rotate(180deg)" : "none",
        transition: "transform 0.15s",
      }}
    >
      <path
        d="M2 3.5L5 6.5L8 3.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  type ModalCol = {
    label: string;
    sortKey?: SortKey;
    isWeather?: boolean;
    align: "left" | "right";
    render: (r: RankRow) => React.ReactNode;
  };

  const cols: ModalCol[] = isGen
    ? [
        {
          label: "PEAK GEN",
          sortKey: "peakDemand",
          align: "left",
          render: (r) => (
            <span className="inline-flex items-center gap-1.5 justify-start whitespace-nowrap">
              <span
                className="text-[12px] font-medium"
                style={{ color: t.peakTextColor }}
              >
                {formatNumber(r.peakDemand, 2)}
                <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">
                  {loadUnit}
                </span>
              </span>
              <PeakBadge date={r.peakDate} slot={r.peakSlot} />
            </span>
          ),
        },
        {
          label: "% OF LOAD",
          sortKey: "pctLoadCovered",
          align: "right",
          render: (r) => {
            const val = r.pctLoadCovered;
            if (val == null)
              return (
                <span className="text-[12px] font-semibold text-[#9CA3AF]">
                  —
                </span>
              );
            return (
              <span className="text-[12px] font-medium text-[#6F6D7C]">
                {val.toFixed(2)}
                <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">
                  %
                </span>
              </span>
            );
          },
        },
        {
          label: "TOTAL GEN",
          sortKey: "netLoad" as SortKey,
          align: "right",
          render: (r) => (
            <span className="text-[12px] font-medium text-[#6F6D7C]">
              {formatNumber(r.netLoad, 2)}
              <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">
                {energyUnit}
              </span>
            </span>
          ),
        },
        {
          label: primaryWeather,
          sortKey: "weather",
          isWeather: true,
          align: "right",
          render: (r) => {
            const val = r[weatherMeta.field] as number | null;
            if (val == null)
              return (
                <span className="text-[12px] font-semibold text-[#9CA3AF]">
                  —
                </span>
              );
            return (
              <span className="text-[12px] font-medium text-[#6F6D7C]">
                {signed(val, 2)}
                <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">
                  {weatherMeta.unit}
                </span>
              </span>
            );
          },
        },
      ]
    : [
        {
          label: "PEAK LOAD",
          sortKey: "peakDemand",
          align: "left",
          render: (r) => (
            <span className="inline-flex items-center gap-1.5 justify-start whitespace-nowrap">
              <span
                className="text-[12px] font-medium"
                style={{ color: t.peakTextColor }}
              >
                {formatNumber(r.peakDemand, 2)}
                <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">
                  {loadUnit}
                </span>
              </span>
              <PeakBadge date={r.peakDate} slot={r.peakSlot} />
            </span>
          ),
        },
        {
          label: "AVG LOAD",
          sortKey: "avgDemand",
          align: "right",
          render: (r) => {
            const purpleColor = THEME_MAP["Net Load"]?.titleColor ?? "#7139D0";
            return (
              <span
                className="text-[12px] font-medium"
                style={{ color: purpleColor }}
              >
                {formatNumber(r.avgDemand, 2)}
                <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">
                  {loadUnit}
                </span>
              </span>
            );
          },
        },
        {
          label: "AVG NET LOAD",
          sortKey: "netLoad" as SortKey,
          align: "right",
          render: (r) => (
            <span className="text-[12px] font-medium text-[#7139D0]">
              {formatNumber(r.netLoad, 2)}
              <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">
                {loadUnit}
              </span>
            </span>
          ),
        },
        {
          label: primaryWeather,
          sortKey: "weather",
          isWeather: true,
          align: "right",
          render: (r) => {
            const val = r[weatherMeta.field] as number | null;
            if (val == null)
              return (
                <span className="text-[12px] font-semibold text-[#9CA3AF]">
                  —
                </span>
              );
            return (
              <span className="text-[12px] font-medium text-[#6F6D7C]">
                {signed(val, 2)}
                <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">
                  {weatherMeta.unit}
                </span>
              </span>
            );
          },
        },
      ];

  return (
    <div
      className="fixed inset-0 z-[1100] bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-[20px] w-[64vw] max-h-[86vh] flex flex-col overflow-hidden shadow-xl"
        style={{ borderTop: `4px solid ${t.titleColor}` }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between pl-6 pr-5 pt-5 pb-4 border-b border-[#F0F4F8]">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm text-white"
              style={{ background: t.iconBackground }}
            >
              {getTitleIcon(title, tag)}
            </div>
            <div className="flex flex-col">
              <span
                className="text-[16px] font-bold tracking-tight leading-tight"
                style={{ color: t.titleColor }}
              >
                {tag || title}
              </span>
              <span className="text-[12px] font-medium text-[#8C8A96] mt-0.5">
                {title} · {rows.length}{" "}
                {rows.length === 1 ? "entry" : "entries"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <svg
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${(entityLabel || "name").toLowerCase()}...`}
                className="w-[200px] pl-8 pr-7 py-1.5 text-[12px] text-[#525062] bg-[#F5F7FA] border border-[#E8EDF2] rounded-lg outline-none focus:border-[#CBD5E1] placeholder:text-[#9CA3AF]"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#525062]"
                  title="Clear"
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-[#8C8A96] hover:bg-[#F5F7FA] hover:text-[#14171F] transition-colors"
              title="Close"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable table */}
        <div
          className="overflow-auto px-6 pb-5 no-copy select-none"
          onCopy={(e) => e.preventDefault()}
          onCut={(e) => e.preventDefault()}
          onContextMenu={(e) => e.preventDefault()}
        >
          <table className="w-full text-left border-collapse">
            <thead className="sticky top-0 bg-white z-10">
              <tr>
                <th className="text-[10px] font-bold text-[#8C8A96] uppercase  pb-3 pt-4 text-left">
                  {entityLabel || "Entity"}
                </th>
                {cols.map((c) => {
                  const active = c.sortKey === sortKey;
                  if (c.isWeather) {
                    return (
                      <th
                        key={c.label}
                        className="text-[10px] font-bold text-[#8C8A96] uppercase  pb-3 pt-4 text-right whitespace-nowrap"
                      >
                        <span className="inline-flex items-center gap-1">
                          <span
                            ref={weatherRef}
                            className="relative inline-flex items-center gap-1 cursor-pointer select-none hover:text-[#525062]"
                            onClick={(e) => {
                              e.stopPropagation();
                              setWeatherOpen((o) => !o);
                            }}
                          >
                            <span>{weatherLabel(primaryWeather)}</span>
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 10 10"
                              fill="none"
                              style={{
                                transform: weatherOpen
                                  ? "rotate(180deg)"
                                  : "none",
                                transition: "transform 0.15s",
                              }}
                            >
                              <path
                                d="M2 3.5L5 6.5L8 3.5"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            {weatherOpen && (
                              <span
                                className="absolute top-full right-0 mt-1 z-[9999] bg-white border border-[#DFEAF2] rounded-lg shadow-lg overflow-hidden"
                                style={{ minWidth: 160 }}
                              >
                                {PRIMARY_WEATHER_OPTIONS.map((opt) => (
                                  <span
                                    key={opt}
                                    onMouseDown={(e) => {
                                      e.stopPropagation();
                                      onWeatherChange(opt);
                                      setWeatherOpen(false);
                                    }}
                                    className={`flex items-center gap-2 px-3 py-2 text-[11px] font-medium cursor-pointer transition-colors whitespace-nowrap ${
                                      opt === primaryWeather
                                        ? "bg-[#FDE7D9] text-[#A44E1E]"
                                        : "text-[#525062] hover:bg-[#F5F7FA]"
                                    }`}
                                  >
                                    {weatherLabel(opt)}
                                  </span>
                                ))}
                              </span>
                            )}
                          </span>
                          <span
                            className="cursor-pointer text-[#8C8A96] hover:text-[#525062]"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSort("weather");
                            }}
                          >
                            <SortIcon active={active} />
                          </span>
                        </span>
                      </th>
                    );
                  }
                  return (
                    <th
                      key={c.label}
                      onClick={() => c.sortKey && toggleSort(c.sortKey)}
                      className={`text-[10px] font-bold uppercase  pb-3 pt-4 whitespace-nowrap select-none ${
                        c.align === "left" ? "text-left" : "text-right"
                      } ${
                        c.sortKey
                          ? "cursor-pointer text-[#8C8A96] hover:text-[#525062]"
                          : "text-[#8C8A96]"
                      } ${active ? "!text-[#525062]" : ""}`}
                    >
                      {c.label}
                      {c.sortKey && <SortIcon active={active} />}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={cols.length + 1}
                    className="py-10 text-center text-[12px] text-[#9CA3AF]"
                  >
                    Loading…
                  </td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={cols.length + 1}
                    className="py-10 text-center text-[12px] text-[#9CA3AF]"
                  >
                    {search.trim() ? "No matches" : "No data available"}
                  </td>
                </tr>
              ) : (
                visibleRows.map((row, i) => (
                  <tr
                    key={i}
                    className="border-t border-[#F0F4F8]"
                  >
                    <td className="py-3 pr-4 max-w-[220px]">
                      <div
                        className={`flex items-center gap-2 min-w-0 ${onRowClick && row.entityId != null ? "cursor-pointer group" : ""}`}
                        onClick={
                          onRowClick && row.entityId != null
                            ? () => onRowClick(row)
                            : undefined
                        }
                      >
                        <span className="text-[10px] text-[#A9A8B1] font-semibold w-5 shrink-0">
                          {i + 1}
                        </span>
                        <span
                          className="w-[4px] h-[18px] rounded-full shrink-0"
                          style={{ backgroundColor: entityPillColor }}
                        />
                        <span
                          className="text-[12px] font-medium text-[#525062] truncate group-hover:underline"
                          title={row.entity}
                        >
                          {row.entity}
                        </span>
                        {onRowClick && row.entityId != null && (
                          <svg
                            className="shrink-0 text-[#8C8A96] group-hover:text-[#14171F] transition-colors ml-1"
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                            <polyline points="15 3 21 3 21 9" />
                            <line x1="10" y1="14" x2="21" y2="3" />
                          </svg>
                        )}
                      </div>
                    </td>
                    {cols.map((c, ci) => (
                      <td
                        key={ci}
                        className={`py-3 pr-4 font-medium text-[12px] ${c.align === "left" ? "text-left" : "text-right"}`}
                      >
                        {c.render(row)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function RankTable({
  title,
  dotColor,
  tag,
  rows,
  primaryWeather,
  onWeatherChange,
  loadUnit = "MW",
  energyUnit = "MWh",
  onRowClick,
  rankView,
  onRankViewChange,
  entityLabel,
  onHeadingClick,
  autoScaleUnit = false,
}: RankTableProps) {
  const [weatherOpen, setWeatherOpen] = React.useState(false);
  const weatherRef = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    if (!weatherOpen) return;
    const handler = (e: MouseEvent) => {
      if (weatherRef.current && !weatherRef.current.contains(e.target as Node))
        setWeatherOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [weatherOpen]);

  const isGen = tag === "Generation Contribution";
  const t = resolveTheme(tag, dotColor);
  const baseHex = t.peakDateBg.startsWith("#")
    ? t.peakDateBg.slice(0, 7)
    : t.peakDateBg;
  const gradientStart = `${baseHex}06`;

  // Custom pill color for the left side of entity names.
  // In Jvvnl dashboard screenshots, entity pills are green for both Load and Generation.
  const entityPillColor = "#047857";

  const cols = isGen
    ? [
        {
          label: "PEAK GEN",
          isWeather: false,
          align: "left" as const,
          render: (r: RankRow) => {
            const s = fmtScaled(r.peakDemand, loadUnit, autoScaleUnit);
            return (
              <span className="inline-flex items-center gap-1.5 justify-start whitespace-nowrap">
                <span
                  className="text-[12px] font-medium"
                  style={{ color: t.peakTextColor }}
                >
                  {s.text}
                  <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">
                    {s.unit}
                  </span>
                </span>
                <PeakBadge date={r.peakDate} slot={r.peakSlot} />
              </span>
            );
          },
        },
        {
          label: "% OF LOAD",
          isWeather: false,
          render: (r: RankRow) => {
            const val = r.pctLoadCovered;
            if (val == null)
              return (
                <span className="text-[12px] font-semibold text-[#9CA3AF]">
                  —
                </span>
              );
            return (
              <span className="text-[12px] font-medium text-[#6F6D7C]">
                {val.toFixed(2)}
                <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">
                  %
                </span>
              </span>
            );
          },
        },
        {
          label: "TOTAL GEN",
          isWeather: false,
          render: (r: RankRow) => {
            const s = fmtScaled(r.netLoad, energyUnit, autoScaleUnit);
            return (
              <span className="text-[12px] font-medium text-[#6F6D7C]">
                {s.text}
                <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">
                  {s.unit}
                </span>
              </span>
            );
          },
        },
        {
          label: primaryWeather,
          isWeather: true,
          render: (r: RankRow) => {
            const meta =
              WEATHER_META[primaryWeather] ?? WEATHER_META["Avg Temp"];
            const val = r[meta.field] as number | null;
            if (val == null)
              return (
                <span className="text-[12px] font-semibold text-[#9CA3AF]">
                  —
                </span>
              );
            return (
              <span className="text-[12px] font-medium text-[#6F6D7C]">
                {signed(val, 2)}
                <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">
                  {meta.unit}
                </span>
              </span>
            );
          },
        },
      ]
    : [
        {
          label: "PEAK LOAD",
          isWeather: false,
          align: "left" as const,
          render: (r: RankRow) => {
            const s = fmtScaled(r.peakDemand, loadUnit, autoScaleUnit);
            return (
              <span className="inline-flex items-center gap-1.5 justify-start whitespace-nowrap">
                <span
                  className="text-[12px] font-medium"
                  style={{ color: t.peakTextColor }}
                >
                  {s.text}
                  <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">
                    {s.unit}
                  </span>
                </span>
                <PeakBadge date={r.peakDate} slot={r.peakSlot} />
              </span>
            );
          },
        },
        {
          label: "AVG LOAD",
          isWeather: false,
          render: (r: RankRow) => {
            const purpleColor = THEME_MAP["Net Load"]?.titleColor ?? "#7139D0";
            const s = fmtScaled(r.avgDemand, loadUnit, autoScaleUnit);
            return (
              <span
                className="text-[12px] font-medium"
                style={{ color: purpleColor }}
              >
                {s.text}
                <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">
                  {s.unit}
                </span>
              </span>
            );
          },
        },
        {
          label: "AVG NET LOAD",
          isWeather: false,
          render: (r: RankRow) => {
            const s = fmtScaled(r.netLoad, loadUnit, autoScaleUnit);
            return (
              <span className="text-[12px] font-medium text-[#7139D0]">
                {s.text}
                <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">
                  {s.unit}
                </span>
              </span>
            );
          },
        },
        {
          label: primaryWeather,
          isWeather: true,
          render: (r: RankRow) => {
            const meta =
              WEATHER_META[primaryWeather] ?? WEATHER_META["Avg Temp"];
            const val = r[meta.field] as number | null;
            if (val == null)
              return (
                <span className="text-[12px] font-semibold text-[#9CA3AF]">
                  —
                </span>
              );
            return (
              <span className="text-[12px] font-medium text-[#6F6D7C]">
                {signed(val, 2)}
                <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">
                  {meta.unit}
                </span>
              </span>
            );
          },
        },
      ];

  return (
    <div
      className="relative rounded-[20px] border flex flex-col gap-0 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 ease-in-out"
      style={{
        boxShadow: "0 1px 4px 0 rgba(0,0,0,0.06)",
        background: `linear-gradient(135deg, ${gradientStart} 0%, #ffffff 40%)`,
        borderColor: t.peakDateBg,
      }}
    >
      {/* Header section (with left accent bar) */}
      <div
        className={`relative pl-5 pr-5 pt-4 pb-3.5 border-b border-[#F0F4F8] ${onHeadingClick ? "cursor-pointer group/header hover:bg-[#FAFBFC] transition-colors duration-150" : ""}`}
        onClick={onHeadingClick}
      >
        {/* Left accent bar (restricted to header height) */}
        <div
          className="absolute left-0 top-0 bottom-0 w-[4px]"
          style={{ background: t.barBackground }}
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm text-white"
              style={{ background: t.iconBackground }}
            >
              {getTitleIcon(title, tag)}
            </div>
            <div className="flex flex-col">
              <span
                className="text-[15px] font-bold tracking-tight leading-tight"
                style={{ color: t.titleColor }}
              >
                {tag || title}
              </span>
              <span className="text-[12px] font-medium text-[#8C8A96] flex items-center gap-1 mt-0.5 leading-none">
                <span
                  className={
                    onHeadingClick ? "group-hover/header:underline" : ""
                  }
                >
                  {title}
                </span>
                {onHeadingClick && (
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="inline-block opacity-60 group-hover/header:opacity-100 transition-opacity"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                )}
                {!onHeadingClick && onRowClick && (
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#8C8A96"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="inline-block opacity-80"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                )}
              </span>
            </div>
          </div>
          {onRankViewChange && rankView ? (
            <div onClick={(e) => e.stopPropagation()}>
              <FilterTab
                options={["Top 3", "Bottom 3"]}
                selected={[rankView === "top" ? "Top 3" : "Bottom 3"]}
                onClick={(opt) =>
                  onRankViewChange(opt === "Top 3" ? "top" : "bottom")
                }
                variant="pill-outline"
              />
            </div>
          ) : (
            tag && (
              <span className="text-[10px] text-[#8C8A96] font-bold  uppercase">
                {tag}
              </span>
            )
          )}
        </div>
      </div>

      {/* Table section */}
      <div
        className="pl-5 pr-5 pb-4 flex flex-col gap-1 no-copy select-none"
        onCopy={(e) => e.preventDefault()}
        onCut={(e) => e.preventDefault()}
        onContextMenu={(e) => e.preventDefault()}
      >
        <table id="metricTable" className="w-full text-left border-collapse">
          <thead>
            <tr>
              <th className="text-[10px] font-bold text-[#8C8A96] uppercase  pb-3 pt-3 text-left whitespace-nowrap">
                {entityLabel || "Entity"}
              </th>
              {cols.map((c) => (
                <th
                  key={c.label}
                  className={`text-[10px] font-bold text-[#8C8A96] uppercase pb-3 pt-3 leading-tight ${"align" in c && c.align === "left" ? "text-left" : "text-right"}`}
                >
                  {c.isWeather ? (
                    <span
                      ref={weatherRef}
                      className="relative inline-flex items-center gap-1 cursor-pointer select-none text-[#8C8A96] hover:text-[#525062]"
                      onClick={() => setWeatherOpen((o) => !o)}
                    >
                      <span>{weatherLabel(primaryWeather)}</span>
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 10 10"
                        fill="none"
                        className="text-[#8C8A96]"
                        style={{
                          transform: weatherOpen ? "rotate(180deg)" : "none",
                          transition: "transform 0.15s",
                        }}
                      >
                        <path
                          d="M2 3.5L5 6.5L8 3.5"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {weatherOpen && (
                        <span
                          className="absolute top-full right-0 mt-1 z-[9999] bg-white border border-[#DFEAF2] rounded-lg shadow-lg overflow-hidden"
                          style={{ minWidth: 160 }}
                        >
                          {PRIMARY_WEATHER_OPTIONS.map((opt) => (
                            <span
                              key={opt}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                onWeatherChange(opt);
                                setWeatherOpen(false);
                              }}
                              className={`flex items-center gap-2 px-3 py-2 text-[11px] font-medium cursor-pointer transition-colors whitespace-nowrap ${
                                opt === primaryWeather
                                  ? "bg-[#FDE7D9] text-[#A44E1E]"
                                  : "text-[#525062] hover:bg-[#F5F7FA]"
                              }`}
                            >
                              <span className="w-3 shrink-0 flex items-center justify-center">
                                {opt === primaryWeather && (
                                  <svg
                                    width="8"
                                    height="8"
                                    viewBox="0 0 8 8"
                                    fill="none"
                                  >
                                    <path
                                      d="M1 4L3 6L7 2"
                                      stroke="#A44E1E"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </span>
                              {weatherLabel(opt)}
                            </span>
                          ))}
                        </span>
                      )}
                    </span>
                  ) : (
                    c.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={cols.length + 1}
                  className="py-6 text-center text-[11px] text-[#9CA3AF]"
                >
                  No data available
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr
                  key={i}
                  className={`border-t border-[#F0F4F8] transition-colors duration-150 ${onRowClick && row.entityId != null ? "cursor-pointer hover:bg-[#F5F7FA] group" : ""}`}
                  onClick={
                    onRowClick && row.entityId != null
                      ? () => onRowClick(row)
                      : undefined
                  }
                >
                  <td className="py-3 pr-2 max-w-[120px] w-[120px]">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="w-[4px] h-[18px] rounded-full shrink-0"
                        style={{ backgroundColor: entityPillColor }}
                      />
                      <span
                        className="text-[12px] font-medium text-[#525062] truncate group-hover:underline"
                        title={row.entity}
                      >
                        {row.entity}
                      </span>
                      {onRowClick && row.entityId != null && (
                        <svg
                          className="shrink-0 text-[#8C8A96] group-hover:text-[#14171F] transition-colors ml-1 opacity-0 group-hover:opacity-100"
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                      )}
                    </div>
                  </td>
                  {cols.map((c, colIndex) => (
                    <td
                      key={colIndex}
                      className={`py-3 pr-2 font-medium text-[12px] ${"align" in c && c.align === "left" ? "text-left" : "text-right"}`}
                    >
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Feeder Summary ──────────────────────────────────────────────────────────

type FeederRow = {
  entity: string;
  entityId?: number | null;
  availablePct: number;
  zerosPct: number;
  nansPct: number;
};

type FeederTableProps = {
  title: string;
  dotColor: string;
  tag?: string;
  rows: FeederRow[];
  onRowClick?: (row: FeederRow) => void;
};

const FEEDER_COLS: {
  label: string;
  key: keyof FeederRow;
  render: (r: FeederRow) => React.ReactNode;
}[] = [
  {
    label: "AVAILABLE %",
    key: "availablePct",
    render: (r) => (
      <span className="text-[12px] font-medium text-[#047857]">
        {r.availablePct.toFixed(2)}
        <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">%</span>
      </span>
    ),
  },
  {
    label: "% OF ZEROS",
    key: "zerosPct",
    render: (r) => (
      <span className="text-[12px] font-medium text-[#6F6D7C]">
        {r.zerosPct.toFixed(2)}
        <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">%</span>
      </span>
    ),
  },
  {
    label: "% OF Missing",
    key: "nansPct",
    render: (r) => (
      <span className="text-[12px] font-medium text-[#6F6D7C]">
        {r.nansPct.toFixed(2)}
        <span className="text-[10px] text-[#9CA3AF] font-medium ml-0.5">%</span>
      </span>
    ),
  },
];

export function FeederTable({
  title,
  dotColor,
  tag,
  rows,
  onRowClick,
}: FeederTableProps) {
  const t = resolveTheme(tag, dotColor);
  const baseHex = t.peakDateBg.startsWith("#")
    ? t.peakDateBg.slice(0, 7)
    : t.peakDateBg;
  const gradientStart = `${baseHex}06`;

  return (
    <div
      className="relative rounded-[20px] border flex flex-col gap-0 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 ease-in-out"
      style={{
        boxShadow: "0 1px 4px 0 rgba(0,0,0,0.06)",
        background: `linear-gradient(135deg, ${gradientStart} 0%, #ffffff 40%)`,
        borderColor: t.peakDateBg,
      }}
    >
      {/* Header section (with left accent bar) */}
      <div className="relative pl-5 pr-5 pt-4 pb-3.5 border-b border-[#F0F4F8]">
        {/* Left accent bar (restricted to header height) */}
        <div
          className="absolute left-0 top-0 bottom-0 w-[4px]"
          style={{ background: t.barBackground }}
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm text-white"
              style={{ background: t.iconBackground }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span
                className="text-[15px] font-bold tracking-tight leading-tight"
                style={{ color: t.titleColor }}
              >
                {title}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Table section */}
      <div
        className="pl-5 pr-5 pb-4 flex flex-col gap-1 no-copy select-none"
        onCopy={(e) => e.preventDefault()}
        onCut={(e) => e.preventDefault()}
        onContextMenu={(e) => e.preventDefault()}
      >
        <table id="feederTable" className="w-full text-left border-collapse">
          <thead>
            <tr>
              <th className="text-[10px] font-bold text-[#8C8A96] uppercase  pb-3 pt-3 text-left">
                Entity
              </th>
              {FEEDER_COLS.map((c) => (
                <th
                  key={c.key}
                  className="text-[10px] font-bold text-[#8C8A96] uppercase  pb-3 pt-3 text-right"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={FEEDER_COLS.length + 1}
                  className="py-6 text-center text-[11px] text-[#9CA3AF]"
                >
                  No data available
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr key={i} className="border-t border-[#F0F4F8]">
                  <td className="py-3 pr-4 max-w-[140px]">
                    <div
                      className={`flex items-center gap-2 min-w-0 ${onRowClick && row.entityId != null ? "cursor-pointer group" : ""}`}
                      onClick={
                        onRowClick && row.entityId != null
                          ? () => onRowClick(row)
                          : undefined
                      }
                    >
                      <span
                        className="w-[4px] h-[18px] rounded-full shrink-0"
                        style={{ backgroundColor: t.titleColor }}
                      />
                      <span
                        className="text-[12px] font-medium text-[#14171F] truncate group-hover:underline"
                        title={row.entity}
                      >
                        {row.entity}
                      </span>
                      {onRowClick && row.entityId != null && (
                        <button
                          className="shrink-0 text-[#8C8A96] group-hover:text-[#14171F] transition-colors cursor-pointer ml-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRowClick(row);
                          }}
                          title={`Open ${row.entity}`}
                          tabIndex={-1}
                        >
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                            <polyline points="15 3 21 3 21 9" />
                            <line x1="10" y1="14" x2="21" y2="3" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                  {FEEDER_COLS.map((c) => (
                    <td
                      key={c.key}
                      className="py-3 pr-4 text-right font-medium text-[12px]"
                    >
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const FEEDER_HIGH_DATA: FeederRow[] = [
  {
    entity: "Heerapura GT-1",
    availablePct: -652.5,
    zerosPct: 0.92,
    nansPct: 0.91,
  },
  {
    entity: "Ramgarh CCGT",
    availablePct: -481.2,
    zerosPct: 0.84,
    nansPct: 0.78,
  },
  {
    entity: "Chhabra Stage-II",
    availablePct: -395.6,
    zerosPct: 0.71,
    nansPct: 0.66,
  },
];

export const FEEDER_LOW_DATA: FeederRow[] = [
  {
    entity: "Heerapura GT-1",
    availablePct: -652.5,
    zerosPct: 0.92,
    nansPct: 0.91,
  },
  {
    entity: "Ramgarh CCGT",
    availablePct: -481.2,
    zerosPct: 0.84,
    nansPct: 0.78,
  },
  {
    entity: "Chhabra Stage-II",
    availablePct: -395.6,
    zerosPct: 0.71,
    nansPct: 0.66,
  },
];

// ── Demand/Gen rank tables ───────────────────────────────────────────────────

export const TOP_3_DATA: RankRow[] = [
  {
    entity: "Heerapura GT-1",
    avgDemand: -652.5,
    peakDemand: 0.92,
    avgTemp: 0.91,
    avgHumidity: null,
    avgSolarIrradiance: null,
    netLoad: 4182,
  },
  {
    entity: "Ramgarh CCGT",
    avgDemand: -481.2,
    peakDemand: 0.84,
    avgTemp: 0.78,
    avgHumidity: null,
    avgSolarIrradiance: null,
    netLoad: 4182,
  },
  {
    entity: "Chhabra Stage-II",
    avgDemand: -395.6,
    peakDemand: 0.71,
    avgTemp: 0.66,
    avgHumidity: null,
    avgSolarIrradiance: null,
    netLoad: 4182,
  },
];

export const BOTTOM_3_DATA: RankRow[] = [
  {
    entity: "Giral Lignite-I",
    avgDemand: -652.5,
    peakDemand: 0.92,
    avgTemp: 0.91,
    avgHumidity: null,
    avgSolarIrradiance: null,
    netLoad: 4182,
  },
  {
    entity: "Kalisindh U-2",
    avgDemand: -481.2,
    peakDemand: 0.84,
    avgTemp: 0.78,
    avgHumidity: null,
    avgSolarIrradiance: null,
    netLoad: 4182,
  },
  {
    entity: "Suratgarh STPS-3",
    avgDemand: -395.6,
    peakDemand: 0.71,
    avgTemp: 0.66,
    avgHumidity: null,
    avgSolarIrradiance: null,
    netLoad: 4182,
  },
];
